/**
 * TierCard Component
 * Displays subscription tier information with upgrade button
 *
 * Requirements: 7.1
 */

import React from 'react';
import './TierCard.css';
import type { SubscriptionTier } from '../types';

interface TierCardProps {
  tier: SubscriptionTier;
  isCurrentTier: boolean;
  onUpgrade: (tier: SubscriptionTier) => void;
  isUpgrading: boolean;
}

const TierCard: React.FC<TierCardProps> = ({
  tier,
  isCurrentTier,
  onUpgrade,
  isUpgrading,
}) => {
  const formatPrice = (price: number | null): string => {
    if (price === null) return 'Custom';
    return price.toFixed(0);
  };

  const formatCostPerCredit = (cost: number | null | undefined): string => {
    if (cost === null || cost === undefined) return '0.00';
    return cost.toFixed(2);
  };

  const getTierKey = (tierName: string): string => {
    if (!tierName) return 'free';
    const name = String(tierName).toLowerCase();
    if (name.includes('starter')) return 'starter';
    if (name.includes('professional')) return 'professional';
    if (name.includes('business')) return 'business';
    if (name.includes('platinum')) return 'platinum';
    if (name.includes('enterprise')) return 'enterprise';
    return 'free';
  };

  const isProfessionalOrAbove = (tierName: string): boolean => {
    if (!tierName) return false;
    const name = String(tierName).toLowerCase();
    return name.includes('professional') ||
           name.includes('business') ||
           name.includes('platinum') ||
           name.includes('enterprise');
  };

  const isBusinessOrAbove = (tierName: string): boolean => {
    if (!tierName) return false;
    const name = String(tierName).toLowerCase();
    return name.includes('business') ||
           name.includes('platinum') ||
           name.includes('enterprise');
  };

  const getButtonText = (): string => {
    if (isUpgrading) return 'Processing...';
    return 'Select Plan';
  };

  const isEnterprise = tier.credits_per_month === null || tier.price_monthly === null;

  if (!tier || !tier.name) {
    return null;
  }

  return (
    <div
      className={`tier-card ${isCurrentTier ? 'current-tier' : ''}`}
      data-tier={getTierKey(tier.name)}
    >
      <div className="tier-card-header">
        <div className="tier-header-top">
          <h3 className="tier-name">{tier.name}</h3>
          {isCurrentTier && <span className="current-badge">Active</span>}
        </div>
        <div className="tier-price">
          {isEnterprise ? (
            <span className="price-amount">Custom</span>
          ) : (
            <>
              <span className="price-amount">€{formatPrice(tier.price_monthly)}</span>
              <span className="price-period">/mo</span>
            </>
          )}
        </div>
      </div>

      <div className="tier-details">
        <div className="tier-credits">
          {isEnterprise
            ? 'Unlimited credits'
            : `${(tier.credits_per_month || 0).toLocaleString()} credits / mo`}
        </div>
        {!isEnterprise && tier.cost_per_credit !== undefined && tier.cost_per_credit !== null && (
          <div className="tier-cost-per-credit">
            €{formatCostPerCredit(tier.cost_per_credit)} per additional credit
          </div>
        )}
        <ul className="tier-features">
          <li>
            {tier.max_active_criteria === 10
              ? 'All 10'
              : `Up to ${tier.max_active_criteria}`} active criteria
          </li>
          {isProfessionalOrAbove(tier.name) && (
            <li>Multi-garment classification</li>
          )}
          {isBusinessOrAbove(tier.name) && (
            <li>Data Dashboard</li>
          )}
          {tier.custom_criteria_enabled && (
            <li>Custom criteria (up to {tier.max_custom_criteria})</li>
          )}
        </ul>
      </div>

      {!isCurrentTier && (
        isEnterprise ? (
          <a
            className="upgrade-btn"
            href="mailto:sales@trialitylabs.com?subject=Interest%20in%20SIFT!"
            aria-label="Contact Sales - Enterprise plan"
          >
            Contact Sales
          </a>
        ) : (
          <button
            className="upgrade-btn"
            onClick={() => onUpgrade(tier)}
            disabled={isUpgrading}
            aria-label={`${getButtonText()} - ${tier.name || 'plan'}`}
          >
            {getButtonText()}
          </button>
        )
      )}
      {isCurrentTier && (
        <div className="current-plan-badge">Current Plan</div>
      )}
    </div>
  );
};

export default TierCard;
